import React, { useEffect, useState } from 'react';
import { Form, Modal, Row, Col, Select, Alert, Space, Typography, theme, Flex, Spin, Card, Tag, Badge, Button, message } from 'antd';
import { CheckCircleOutlined, ExclamationCircleFilled, ExclamationCircleOutlined, CopyOutlined, CheckCircleFilled } from '@ant-design/icons';

const DIDOMI_ORGANISATIONS_FOR_QA_TESTING = [{ddm_organization_id: 'sourcepoint-master'}, {ddm_organization_id: 'integrated-qa'}, {ddm_organization_id: 'tom-test'}];

function DidomiMigrationModal(props) {
  const { visible, onConfirm, onCancel, migrationInfo, didomiOrganizations, entity, loading, migrationRecord, migrationError, setMigrationError} = props;
  const [form] = Form.useForm();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedDidomiOrg, setSelectedDidomiOrg] = useState(null);
  const { token } = theme.useToken();

  useEffect(() => {
    if (!visible) {
      setConfirmationOpen(false);
      setSelectedDidomiOrg(null);
      form.resetFields();
    }
  }, [visible, form]);

  const handleOk = async () => {
    if (migrationError) {
      setConfirmationOpen(false);
      setMigrationError(null);
      return;
    } else if(migrationInfo) {
      onCancel();
      return
    }
    if (confirmationOpen) {
      if (selectedDidomiOrg) {
        await onConfirm(selectedDidomiOrg);
        setConfirmationOpen(false);
        setSelectedDidomiOrg(null);
        form.resetFields();
      }
    } else {
      try {
        const values = await form.validateFields(['didomiOrganizationId']);
        setSelectedDidomiOrg(values.didomiOrganizationId);
        setConfirmationOpen(true);
      } catch (errorInfo) {
        // Validation failed; Ant Design Form will display the error on the Select control.
      }
    }
  };

  const handleCancel = () => {
    if (confirmationOpen) {
      setConfirmationOpen(false);
    } else {
      onCancel();
    }
  };

  const loadingScreen = (
    <Row justify="center" align="middle" style={{ minHeight: 240 }}>
      <Col style={{ textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: token.margin, fontSize: token.fontSizeLG, fontWeight: 600 }}>Migrating to Didomi...</div>
        <div style={{ marginTop: token.marginXS, color: token.colorTextSecondary }}>Please wait while we migrate your vendor list.</div>
      </Col>
    </Row>
  );

  const unmapped = migrationInfo?.unmapped || {};
  const vendorCounts = unmapped.vendorCounts || {};

  const copyUnmappedList = async (label, sections) => {
    const copyText = sections
      .filter(({ items }) => Array.isArray(items) && items.length > 0)
      .map(({ sublabel, items }) => {
        const names = items
          .map((item) => item?.name)
          .filter(Boolean);

        if (!names.length) return null;

        const numberedList = names
          .map((name, index) => `${index + 1}. ${name}`)
          .join('\n');

        return `list of unmapped ${names.length} ${sublabel}:\n${numberedList}`;
      })
      .filter(Boolean)
      .join('\n\n');

    try {
      await navigator.clipboard.writeText(copyText);
      message.success(`Unmapped ${label} copied to clipboard.`);
    } catch (err) {
      message.error(`Failed to copy unmapped ${label}.`);
    }
  };

  const successScreen = migrationInfo && (
    <Row gutter={[token.marginMD, token.margin]}>
      <Col span={24}>
       <Flex justify="flex-start" vertical align='center' gap={token.margin}>
          <CheckCircleFilled
            style={{
              fontSize: token.marginXL,
              marginRight: token.margin,
              color: token.colorSuccess,
            }}
          />
          <Typography.Title level={4}>Migration Successful</Typography.Title>
          <Typography.Text type="secondary">
            <>Successfully migrated {entity} <strong>{migrationRecord?.name}</strong> to Didomi organization <strong>{migrationInfo.didomiOrganizationId}</strong>.</>
          </Typography.Text>
      </Flex>
      </Col>
      {vendorCounts?.total > 0 ? (
        <Col span={24}>
          <Card
            title={
              <>
                <ExclamationCircleOutlined style={{ color: token.colorWarning }} /> Not migrated successfully
              </>
            }
            extra={
              <Space>
                <Tag color="orange">{vendorCounts.total} vendors</Tag>
                <Button
                  icon={<CopyOutlined />}
                  size="small"
                  onClick={() => copyUnmappedList('vendors', [
                    { sublabel: 'IAB vendors', items: unmapped.unmappedIabVendors },
                    { sublabel: 'Custom vendors', items: unmapped.unmappedCustomVendors },
                  ])}
                />
              </Space>
            }
          >
            <Row gutter={[token.marginMD, token.margin]}>
              <Col span={24}>
                  <Typography.Paragraph>The following vendors could not be migrated. They may need to be configured manually in Didomi.</Typography.Paragraph>
                </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }} className='unmapped-list'>
                  <Typography.Text>IAB <Tag color="orange">{vendorCounts.iab ?? 0}</Tag></Typography.Text>
                  {unmapped.unmappedIabVendors?.map((vendor) =>(<Badge color="orange" text={vendor.name} />))}
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }} className='unmapped-list'>
                  <Typography.Text>Custom <Tag color="blue">{vendorCounts.custom ?? 0}</Tag></Typography.Text>
                  {unmapped.unmappedCustomVendors?.map((vendor) =>(<Badge color="blue" text={vendor.name} />))}
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      ) : (
        <Col span={24}>
           <Card title={<><CheckCircleOutlined  style={{color: token.colorSuccess}}/> Migration Successful!</>}>
           <Typography.Text type="success"><CheckCircleOutlined/>{" "}All vendors in this list were successfully migrated.</Typography.Text>
           </Card>
        </Col>
      )}
      {unmapped.purposes?.length > 0 || unmapped.customPurposes?.length > 0 || unmapped.stacks?.length > 0 ? (
        <Col span={24}>
          <Card
            title={
              <>
                <ExclamationCircleOutlined style={{ color: token.colorWarning }} /> Not migrated successfully
              </>
            }
            extra={
              <Space>
                <Tag color="orange">{unmapped.purposes?.length + unmapped.customPurposes?.length + unmapped.stacks?.length} purposes</Tag>
                <Button
                  icon={<CopyOutlined />}
                  size="small"
                  onClick={() => copyUnmappedList('purposes', [
                    { sublabel: 'IAB purposes', items: unmapped.purposes },
                    { sublabel: 'Custom purposes', items: unmapped.customPurposes },
                    { sublabel: 'stacks', items: unmapped.stacks },
                  ])}
                />
              </Space>
            }
          >
            <Row gutter={[token.marginMD, token.margin]}>
              <Col span={24}>
                  <Typography.Paragraph>The following purposes / stacks could not be migrated. They may need to be configured manually in Didomi.</Typography.Paragraph>
                </Col>
              <Col span={8}>
                <Space direction="vertical" style={{ width: '100%' }} className='unmapped-list'>
                  <Typography.Text>IAB <Tag color="orange">{unmapped.purposes?.length ?? 0}</Tag></Typography.Text>
                  {unmapped.purposes?.map((purpose) =>(<Badge color="orange" text={purpose.name} />))}
                </Space>
              </Col>
              <Col span={8}>
                <Space direction="vertical" style={{ width: '100%' }} className='unmapped-list'>
                  <Typography.Text>Custom <Tag color="blue">{unmapped.customPurposes?.length ?? 0}</Tag></Typography.Text>
                  {unmapped.customPurposes?.map((purpose) =>(<Badge color="blue" text={purpose.name} />))}
                </Space>
              </Col>
              <Col span={8}>
                <Space direction="vertical" style={{ width: '100%' }} className='unmapped-list'>
                  <Typography.Text>Stacks <Tag color="magenta">{unmapped.stacks?.length ?? 0}</Tag></Typography.Text>
                  {unmapped.stacks?.map((stack) =>(<Badge color="magenta" text={stack.name} />))}
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      ) : (
        <Col span={24}>
          <Card title={<><CheckCircleOutlined  style={{color: token.colorSuccess}}/> Migration Successful!</>}>
            <Typography.Text type="success"><CheckCircleOutlined/>{" "}All purposes in this list were successfully migrated.</Typography.Text>
         </Card>
        </Col>
      )}
    </Row>
  )
  const errorScreen = migrationError ? (
    <Row gutter={[token.marginMD, token.margin]}>
      <Col span={24}>
        <Flex justify="flex-start" vertical align='center' gap={token.margin}>
          <ExclamationCircleFilled
            style={{
              fontSize: token.marginXL,
              marginRight: token.margin,
              color: token.colorError,
            }}
          />
          <Typography.Title level={4}>Migration Failed</Typography.Title>           
          <Alert type="error" message="Unable to connect to Didomi API. Please check your network connection and try again."/>
        </Flex>
      </Col>
    </Row>
  ) : null;
  return (
    <Modal
      className='didomi-migration-modal'
      width={700}
      visible={visible}
      title="Migrate to Didomi"
      onOk={handleOk}
      okText={
        migrationError
          ? "Retry"
          : migrationInfo
            ? "Done"
            : confirmationOpen
              ? "Confirm"
              : "Continue"
      }
      cancelText={migrationInfo ? "Close" : "Back"}
      cancelButtonProps={{ disabled: loading, style: { display: migrationInfo ? 'none' : 'inline-block' } }}
      onCancel={handleCancel}
      okButtonProps={{ loading }}
    >
      {loading ? (
        loadingScreen
      ) : migrationError ? (
        errorScreen
      ) : migrationInfo ? (
        successScreen
      ) : confirmationOpen ? (
        <Row gutter={[token.marginMD, token.margin]}>
          <Col span={24}>
            <Flex justify="flex-start">
              <ExclamationCircleFilled
                style={{
                  fontSize: token.marginLG,
                  marginRight: token.margin,
                  color: token.colorError,
                }}
              />
              <Space direction="vertical" size={0}>
                <Typography.Title level={4}>Are you sure?</Typography.Title>
                <Typography.Text type="secondary">
                  This is your last chance to review
                </Typography.Text>
              </Space>
            </Flex>
          </Col>
          <Col span={24}>
            <Flex
              style={{
                background: token.colorFillTertiary,
                borderRadius: token.borderRadius,
                padding: token.paddingMD,
              }}
              justify="space-between"
              align="center"
            >
              <Typography.Text type="secondary">Destination: </Typography.Text>
              <Typography.Text strong>{selectedDidomiOrg}</Typography.Text>
            </Flex>
          </Col>
          <Col span={24}>
            <Alert
              showIcon
              message={
                <>
                  Note: This action can be performed multiple times. Each
                  execution will create duplicates on Didomi.
                </>
              }
              type="warning"
            />
          </Col>
        </Row>
      ) : (
        <Row gutter={[20, 16]}>
          <Col span={24}>
            <Alert
              showIcon
              message={
                <>
                  This action pushes the migration of this {entity} to Didomi.
                  It can be performed multiple times — each execution will{" "}
                  <b>create duplicates</b> on Didomi.
                </>
              }
              type="warning"
            />
          </Col>
          <Col span={24}>
            <Form form={form} layout="vertical" requiredMark={false}>
              <Form.Item
                label="Select Didomi Organization"
                name="didomiOrganizationId"
                rules={[
                  {
                    required: true,
                    message: "Please select a Didomi Organization",
                  },
                ]}
              >
                <Select
                  style={{width: 320}}
                  placeholder="-- Select Didomi Organization --"
                  options={DIDOMI_ORGANISATIONS_FOR_QA_TESTING.map((org) => ({
                    label: org.ddm_organization_id,
                    value: org.ddm_organization_id,
                  }))}
                  onChange={(value) => setSelectedDidomiOrg(value)}
                />
              </Form.Item>
            </Form>
          </Col>
        </Row>
      )}
    </Modal>
  );
}

export default DidomiMigrationModal;
